import React from 'react';

export default function TradeVisualizer({ entryPrice, currentPrice, stopLoss, profitTarget }) {
  // 1. Handle missing data gracefully
  if (!entryPrice || !currentPrice || !stopLoss || !profitTarget) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  // 2. Determine the mathematical range
  const min = Math.min(stopLoss, currentPrice); // Allows dot to go past SL if slippage occurs
  const max = Math.max(profitTarget, currentPrice); // Allows dot to go past Target if holding
  const range = max - min;

  if (range <= 0) return null;

  // 3. Calculate positioning percentages (clamped between 0 and 100)
  const getPercent = (value) => Math.max(0, Math.min(100, ((value - min) / range) * 100));

  const entryPct = getPercent(entryPrice);
  const currentPct = getPercent(currentPrice);
  const slPct = getPercent(stopLoss);
  const tpPct = getPercent(profitTarget);

  // Determine if currently profitable to color the tracker
  const isProfitable = currentPrice >= entryPrice;

  return (
    <div className="flex flex-col gap-1 w-full">
      {/* Caption — the bar's endpoints are the GTT stop-loss/target bracket, not a
          price range, and that isn't self-evident at a glance. Shown as static text
          (not just a hover title) so it reads the same on touch devices. */}
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-medium">
        Stop-loss → target
      </span>

      <div className="flex items-center w-full gap-2 text-[10px] font-mono">
        {/* Stop Loss Label */}
        <span
          className={`${stopLoss > entryPrice? 'text-win' : 'text-loss'} font-semibold w-14 text-right shrink-0`}
          title={`Stop-loss: ${stopLoss.toFixed(2)}`}
        >
          {stopLoss.toFixed(2)}
        </span>

        {/* Visual Bar Container */}
        <div className="relative flex-1 h-1 bg-surface-hover rounded-full overflow-visible flex items-center">

          {/* Risk Zone (Entry to SL) */}
          <div
            className="absolute h-full bg-loss/20 rounded-l-full"
            style={{ left: `${slPct}%`, width: `${Math.max(0, entryPct - slPct)}%` }}
          />

          {/* Profit Zone (Entry to Target) */}
          <div
            className="absolute h-full bg-win/20 rounded-r-full"
            style={{ left: `${entryPct}%`, width: `${Math.max(0, tpPct - entryPct)}%` }}
          />

          {/* Entry Price Marker */}
          <div
            className="absolute w-0.5 h-3 bg-muted-foreground/50 transform -translate-x-1/2 z-10"
            style={{ left: `${entryPct}%` }}
            title={`Entry: ${entryPrice.toFixed(2)}`}
          />

          {/* Current Price Tracker */}
          <div
            className={`absolute w-2.5 h-2.5 rounded-full border shadow-xs transform -translate-x-1/2 z-20 transition-all duration-300 ${
              isProfitable ? 'bg-win border-win/20' : 'bg-loss border-loss/20'
            }`}
            style={{ left: `${currentPct}%` }}
            title={`Current: ${currentPrice.toFixed(2)}`}
          />
        </div>

        {/* Target Label */}
        <span
          className="text-win font-semibold w-14 text-left shrink-0"
          title={`Target: ${profitTarget.toFixed(2)}`}
        >
          {profitTarget.toFixed(2)}
        </span>
      </div>
    </div>
  );
}